import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { convsAPI } from '../../utils/api';
import { formatLastSeen, groupMessagesByDate } from '../../utils/helpers';
import Avatar from '../UI/Avatar';
import MessageBubble from './MessageBubble';

export default function ChatWindow({ onOpenProfile, onStartCall, onBack, showBackButton = false }) {
  const { user }          = useAuth();
  const { activeConvId, conversations, messages, loading, sendMessage, pollMessages, setActiveConvId, editMessage, reactMessage, unreactMessage } = useChat();
  const [text, setText]   = useState('');
  const [replyTo, setReplyTo]       = useState(null);
  const [showEmoji, setShowEmoji]   = useState(false);
  const [sending, setSending]       = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const [recording, setRecording]   = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef  = useRef(null);
  const textareaRef     = useRef(null);
  const fileInputRef    = useRef(null);
  const pollRef         = useRef(null);
  const mediaRecRef     = useRef(null);
  const recTimerRef     = useRef(null);
  const chunksRef       = useRef([]);

  const conv      = conversations.find(c => c.id === activeConvId);
  const msgs      = messages[activeConvId] || [];
  const isGroup   = conv?.type === 'group';
  const otherUser = conv?.other_user;

  const isActiveConv = activeConvId !== null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs.length]);

  // Polling is now handled centrally in ChatContext for a more "WhatsApp-like" experience.
  // We keep the effect to mark as read when activeConvId changes.
  useEffect(() => {
    if (activeConvId) {
      convsAPI.markRead(activeConvId).catch(() => {});
    }
  }, [activeConvId]);

  const handleSend = async () => {
    if ((!text.trim() && !replyTo) || sending) return;
    setSending(true);
    try {
      await sendMessage(activeConvId, { type: 'text', content: text.trim(), reply_to: replyTo?.id || null });
      setText('');
      setReplyTo(null);
      if (textareaRef.current) { textareaRef.current.style.height = 'auto'; }
    } catch {}
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image'
               : file.type.startsWith('video/') ? 'video'
               : file.type.startsWith('audio/') ? 'audio'
               : 'file';
    setSending(true);
    try {
      await sendMessage(activeConvId, { type, file, content: text.trim() || null, reply_to: replyTo?.id || null });
      setText(''); setReplyTo(null);
    } catch {}
    setSending(false);
  };

  // Fixed voice recording — single recording, no repeat sends
  const startVoiceRecord = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg' });
      mediaRecRef.current = recorder;

      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (chunksRef.current.length === 0) return;
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const ext  = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: mimeType });
        chunksRef.current = []; // Clear to prevent double-sends
        await sendMessage(activeConvId, { type: 'voice', file });
      };

      recorder.start();
      setRecording(true);
      setRecordSecs(0);
      recTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);

      // Auto-stop after 60s
      setTimeout(() => { if (mediaRecRef.current?.state === 'recording') stopVoiceRecord(); }, 60000);
    } catch {
      alert('Microphone access denied');
    }
  };

  const stopVoiceRecord = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecordSecs(0);
    if (mediaRecRef.current?.state === 'recording') {
      mediaRecRef.current.stop();
      mediaRecRef.current = null;
    }
  };

  const cancelVoiceRecord = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecordSecs(0);
    chunksRef.current = []; // Discard
    if (mediaRecRef.current?.state === 'recording') {
      const mr = mediaRecRef.current;
      mediaRecRef.current = null;
      mr.onstop = () => {}; // Prevent sending
      mr.stop();
      mr.stream?.getTracks().forEach(t => t.stop());
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const statusText = () => {
    if (isGroup) {
      const count = conv?.participants?.length || conv?.members?.length || 0;
      return count ? `${count} members` : 'Group';
    }
    if (!otherUser) return '';
    if (otherUser.status === 'online') return '🟢 Online';
    return formatLastSeen(otherUser.last_seen);
  };

  // Filter messages for search
  const filteredMsgs = searchQuery
    ? msgs.filter(m => m.content?.toLowerCase().includes(searchQuery.toLowerCase()))
    : msgs;
  const grouped = groupMessagesByDate(filteredMsgs);

  if (!activeConvId) {
    return (
      <div className="chat-area">
        <div className="chat-placeholder">
          <div style={{ fontSize: 80 }}>💬</div>
          <h2>NChat</h2>
          <p>Send and receive messages privately</p>
          <p style={{ fontSize: 12, marginTop: 8 }}>🔒 End-to-end communication</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area" onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
      style={{ outline: dragOver ? '3px dashed var(--primary)' : 'none' }}>

      {/* Header */}
      <div className="chat-header">
        {showBackButton && (
          <button className="icon-btn dark" onClick={onBack} style={{ flexShrink: 0 }}>◀</button>
        )}
        <div className="conv-avatar" style={{ cursor: 'pointer' }} onClick={() => onOpenProfile(conv)}>
          <Avatar user={isGroup ? { name: conv?.name } : otherUser} size={38} />
          {!isGroup && otherUser?.status === 'online' && <span className="online-dot" />}
        </div>
        <div className="chat-header-info" onClick={() => onOpenProfile(conv)}>
          <h3>{conv?.name || 'Chat'}</h3>
          <p>{statusText()}</p>
        </div>
        <div className="chat-header-actions">
          <button className="icon-btn dark" title="Voice Call" onClick={() => onStartCall(conv, 'voice')}>📞</button>
          <button className="icon-btn dark" title="Video Call" onClick={() => onStartCall(conv, 'video')}>📹</button>
          <button className="icon-btn dark" title="Search messages" onClick={() => { setSearchOpen(o => !o); setSearchQuery(''); }}>🔍</button>
          <MoreMenu conv={conv} />
        </div>
      </div>

      {/* In-chat search bar */}
      {searchOpen && (
        <div style={{ padding: '8px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            autoFocus
            className="search-input"
            placeholder="Search in conversation..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <button onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#667781' }}>✕</button>
        </div>
      )}

      {/* Messages */}
      <div className="messages-wrap">
        {loading && <div style={{ textAlign: 'center', padding: 16 }}><div className="spinner" /></div>}
        {searchQuery && filteredMsgs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: '#667781' }}>No messages match "{searchQuery}"</div>
        )}
        {grouped.map((group, gi) => (
          <React.Fragment key={gi}>
            <div className="date-divider"><span>{group.date}</span></div>
            {group.messages.map(msg => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                convId={activeConvId}
                conversations={conversations}
                onReply={setReplyTo}
                onEdit={editMessage}
                onReact={reactMessage}
                onUnreact={unreactMessage}
                isGroup={isGroup}
              />
            ))}
          </React.Fragment>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="message-input-area">
        {replyTo && (
          <div className="reply-bar">
            <div className="reply-bar-content">
              <h5>↩️ Reply to {replyTo.sender_name}</h5>
              <p>{replyTo.content || `[${replyTo.type}]`}</p>
            </div>
            <button onClick={() => setReplyTo(null)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#667781' }}>✕</button>
          </div>
        )}

        {recording ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: '#FEE2E2', borderRadius: 24, margin: '0 8px 8px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', animation: 'pulse 1s infinite' }} />
            <span style={{ flex: 1, fontWeight: 600, color: '#B91C1C', fontSize: 14 }}>
              🎙 Recording... {String(Math.floor(recordSecs / 60)).padStart(2,'0')}:{String(recordSecs % 60).padStart(2,'0')}
            </span>
            <button onClick={cancelVoiceRecord} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#EF4444' }} title="Cancel">✕</button>
            <button onClick={stopVoiceRecord} style={{ background: '#128C7E', border: 'none', borderRadius: 20, padding: '6px 16px', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Send</button>
          </div>
        ) : (
          <div className="input-row">
            <div className="input-wrap">
              <button className="icon-btn dark" title="Emoji" onClick={() => setShowEmoji(!showEmoji)}>😊</button>
              <textarea
                ref={textareaRef}
                className="message-textarea"
                placeholder="Type a message"
                rows={1}
                value={text}
                onChange={e => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
                onKeyDown={handleKeyDown}
              />
              <button className="icon-btn dark" title="Attach file" onClick={() => fileInputRef.current?.click()}>📎</button>
            </div>
            {text.trim()
              ? <button className="send-btn" onClick={handleSend} disabled={sending}>{sending ? '⏳' : '➤'}</button>
              : <button className="send-btn" onMouseDown={startVoiceRecord} title="Hold to record">🎤</button>
            }
          </div>
        )}

        {showEmoji && (
          <div className="emoji-picker-wrap">
            <EmojiPanel onSelect={e => { setText(t => t + e); setShowEmoji(false); textareaRef.current?.focus(); }} />
          </div>
        )}
      </div>

      <input ref={fileInputRef} type="file" style={{ display: 'none' }}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        onChange={e => { handleFileUpload(e.target.files[0]); e.target.value = ''; }} />

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.4; } }
      `}</style>
    </div>
  );
}

const EMOJI_LIST = ['😀','😂','😍','🥰','😎','🤔','😭','😡','🥳','😴','👍','👎','❤️','🔥','💯','🎉','🙏','💪','👀','🤝','😊','😋','🤩','😇','🥺','😏','😅','😆','😃','🤣'];
function EmojiPanel({ onSelect }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 12, boxShadow: '0 4px 20px rgba(0,0,0,.15)', display: 'flex', flexWrap: 'wrap', gap: 6, width: 260 }}>
      {EMOJI_LIST.map(e => (
        <button key={e} onClick={() => onSelect(e)}
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, padding: 2 }}>{e}</button>
      ))}
    </div>
  );
}

function MoreMenu({ conv }) {
  const { loadConversations } = useChat();
  const [open, setOpen] = useState(false);

  const handle = async (action) => {
    setOpen(false);
    if (!conv) return;
    if (action === 'mute')    await convsAPI.mute(conv.id, conv.is_muted ? 0 : 1);
    if (action === 'archive') await convsAPI.archive(conv.id, 1);
    await loadConversations();
  };

  return (
    <div style={{ position: 'relative' }}>
      <button className="icon-btn dark" onClick={() => setOpen(o => !o)}>⋮</button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setOpen(false)} />
          <div className="context-menu" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100 }}>
            <div className="context-menu-item" onClick={() => handle('mute')}>
              {conv?.is_muted ? '🔔 Unmute' : '🔇 Mute'}
            </div>
            <div className="context-menu-item" onClick={() => handle('archive')}>📦 Archive</div>
          </div>
        </>
      )}
    </div>
  );
}
